import { useMemo } from 'react';
import { Box, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import type { RockUCSGeneral } from '../../../../types/rockUcs';
import { buildFailureDescriptionPreview } from '../computations';
import { FAILURE_MODE_OPTIONS, FAILURE_MODES_WITH_SECONDARY, FAILURE_PLANE_OPTIONS } from '../rockUcsFields';
import { inputSx, paperSx, SectionHeader, unit } from '../sectionStyles';
import { FailureModeReference } from './FailureModeReference';
import { ObservationMediaPanel } from '../../../../components/observations/ObservationMediaPanel';

interface Props {
  general: RockUCSGeneral;
  onChange: (field: keyof RockUCSGeneral, value: number | string | null) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

/**
 * Failure Mode & Observations (mockup: Rock UCS result entry 2.png).
 * Mode + plane selections drive the auto-generated failure description
 * (Specimen sheet AC64–AC68 templates); the description can be overridden.
 */
export const FailureModeSection = ({ general, onChange, readOnly, workOrderDetailKey }: Props) => {
  const autoDescription = useMemo(() => buildFailureDescriptionPreview({
    mode: general.failureMode,
    primaryPlaneType: general.primaryPlaneType,
    primaryPlaneAngleDeg: general.primaryPlaneAngleDeg,
    secondaryPlaneType: general.secondaryPlaneType,
    secondaryPlaneAngleDeg: general.secondaryPlaneAngleDeg,
  }), [general.failureMode, general.primaryPlaneType, general.primaryPlaneAngleDeg, general.secondaryPlaneType, general.secondaryPlaneAngleDeg]);

  const hasSecondary = general.failureMode ? FAILURE_MODES_WITH_SECONDARY.has(general.failureMode) : false;

  return (
    <Box sx={{ display: 'grid', rowGap: 1.5 }}>
      <Paper variant="outlined" sx={paperSx}>
        <SectionHeader label="5. Failure Mode" />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={general.failureMode}
          onChange={(_, v) => { if (!readOnly) onChange('failureMode', v); }}
          sx={{ flexWrap: 'wrap', mb: 1.5, '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.72rem', py: 0.4 } }}
        >
          {FAILURE_MODE_OPTIONS.map((m) => (
            <ToggleButton key={m} value={m}>{m}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.25 }}>
          <TextField
            select size="small" label="Primary Plane"
            value={general.primaryPlaneType ?? ''}
            onChange={(e) => onChange('primaryPlaneType', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          >
            <MenuItem value="">—</MenuItem>
            {FAILURE_PLANE_OPTIONS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <NumericTextField
            size="small" label="Angle to Load Axis"
            value={general.primaryPlaneAngleDeg}
            onChange={(v) => onChange('primaryPlaneAngleDeg', v)}
            InputProps={{ endAdornment: unit('°'), readOnly }} sx={inputSx}
          />
          <TextField
            select size="small" label="Secondary Plane (optional)"
            value={general.secondaryPlaneType ?? ''}
            onChange={(e) => onChange('secondaryPlaneType', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
            disabled={!hasSecondary}
          >
            <MenuItem value="">—</MenuItem>
            {FAILURE_PLANE_OPTIONS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <NumericTextField
            size="small" label="Secondary Angle"
            value={general.secondaryPlaneAngleDeg}
            onChange={(v) => onChange('secondaryPlaneAngleDeg', v)}
            InputProps={{ endAdornment: unit('°'), readOnly }} sx={inputSx}
            disabled={!hasSecondary}
          />
        </Box>

        {/* Failure-mode reference diagrams — the selected mode highlights */}
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
          <FailureModeReference selectedMode={general.failureMode} />
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="6. Failure Description" />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.75 }}>
            Auto-generated: {autoDescription ?? '— select a failure mode —'}
          </Typography>
          <TextField
            size="small" fullWidth multiline minRows={2}
            label="Override (leave empty to use the auto-generated sentence)"
            value={general.failureDescriptionOverride ?? ''}
            onChange={(e) => onChange('failureDescriptionOverride', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
        </Paper>

        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="7. Additional Observations (optional)" />
          <TextField
            size="small" fullWidth multiline minRows={3}
            placeholder="Internal observations about specimen, test conditions, or failure characteristics — not printed on the report."
            value={general.additionalObservations ?? ''}
            onChange={(e) => onChange('additionalObservations', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
          {workOrderDetailKey && (
            <ObservationMediaPanel workOrderDetailKey={workOrderDetailKey} readOnly={readOnly} />
          )}
        </Paper>
      </Box>
    </Box>
  );
};
